import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { authApi } from '../api/authApi';
import { normalizeAdminUser } from '../utils/permissions';

const setCookie = (name, value, days = 7) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires.toUTCString()};path=/`;
};

const getCookie = (name) => {
  const nameEQ = `${name}=`;
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) {
      const raw = c.substring(nameEQ.length, c.length);
      try {
        return decodeURIComponent(raw);
      } catch {
        return raw;
      }
    }
  }
  return null;
};

const deleteCookie = (name) => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
};

const USER_STORAGE_KEY = 'adminUser';

const persistUser = (user) => {
  try {
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
  } catch (e) {
    console.warn('Could not persist admin user to localStorage', e);
  }
  try {
    setCookie(
      'adminUser',
      JSON.stringify({
        id: user?.id,
        email: user?.email,
        type: user?.type,
        is_superadmin: user?.is_superadmin,
      }),
      7
    );
  } catch (_) {
    /* ignore */
  }
};

const readStoredUser = () => {
  try {
    const raw = localStorage.getItem(USER_STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch (_) {
    /* ignore */
  }
  try {
    const cookie = getCookie('adminUser');
    if (cookie) return JSON.parse(cookie);
  } catch (_) {
    /* ignore */
  }
  return null;
};

const clearStoredAuth = () => {
  deleteCookie('adminToken');
  deleteCookie('adminUser');
  try {
    localStorage.removeItem(USER_STORAGE_KEY);
  } catch (_) {
    /* ignore */
  }
};

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const extractUserFromLogin = (data, email) => {
  if (data?.user) return normalizeAdminUser(data.user, email);
  return normalizeAdminUser(
    {
      id: data.id,
      email,
      type: data.type,
      is_superadmin: data.is_superadmin,
      features: data.features,
      phone: data.phone,
      name: data.name,
      hotels: data.hotels,
      is_active: data.is_active,
    },
    email
  );
};

const extractUserFromProfile = (data) => {
  const raw = data?.user || data?.data?.user || data?.data || data;
  return normalizeAdminUser(raw);
};

const looksLikeAuthFailure = (data, status) => {
  if (status === 401) return true;
  const msg = String(data?.message || data?.error || '').toLowerCase();
  return (
    msg.includes('invalid token') ||
    msg.includes('token expired') ||
    msg.includes('unauthorized') ||
    msg.includes('not authenticated') ||
    msg.includes('authentication required')
  );
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);
  const booted = useRef(false);

  const applySession = useCallback((nextToken, nextUser) => {
    setCookie('adminToken', nextToken, 7);
    persistUser(nextUser);
    setToken(nextToken);
    setUser(nextUser);
    setIsAuthenticated(true);
  }, []);

  const clearSession = useCallback(() => {
    clearStoredAuth();
    setUser(null);
    setToken(null);
    setIsAuthenticated(false);
  }, []);

  const refreshProfile = useCallback(
    async (tokenOverride) => {
      const activeToken = tokenOverride || getCookie('adminToken');
      if (!activeToken) {
        // Only clear if we truly have no token — do not wipe mid-refresh races
        return { success: false, message: 'No token' };
      }
      try {
        const data = await authApi.getAdminProfile(activeToken);

        if (data?.status === false) {
          if (looksLikeAuthFailure(data, 401)) {
            clearSession();
            return { success: false, message: data.message || 'Unauthorized' };
          }
          // Keep existing session (profile endpoint glitch / unknown action)
          return { success: false, message: data.message || 'Failed to load profile', keepSession: true };
        }

        const nextUser = extractUserFromProfile(data);
        // Bad/empty profile payload — keep cached user, do not logout
        if (nextUser.id == null && !nextUser.email) {
          return { success: false, message: 'Empty profile', keepSession: true };
        }
        if (nextUser.is_active === false) {
          clearSession();
          return { success: false, message: 'Account is deactivated' };
        }
        applySession(activeToken, nextUser);
        return { success: true, user: nextUser };
      } catch (error) {
        if (looksLikeAuthFailure(error.data, error.status)) {
          clearSession();
          return { success: false, message: error.message || 'Unauthorized' };
        }
        // Network / 5xx / 404 — stay logged in with cached user
        console.warn('getAdminProfile failed; keeping local session', error);
        return { success: false, message: error.message || 'Failed to load profile', keepSession: true };
      }
    },
    [applySession, clearSession]
  );

  useEffect(() => {
    if (booted.current) return;
    booted.current = true;

    const boot = async () => {
      const adminToken = getCookie('adminToken');
      const storedUser = readStoredUser();

      if (!adminToken) {
        setLoading(false);
        return;
      }

      // Restore session immediately so refresh never flashes login
      setToken(adminToken);
      setIsAuthenticated(true);
      if (storedUser) {
        setUser(normalizeAdminUser(storedUser));
      }

      await refreshProfile(adminToken);
      setLoading(false);
    };

    boot();
  }, [refreshProfile]);

  const login = async (email, password) => {
    try {
      const data = await authApi.login(email, password);

      if (data.status) {
        const nextUser = extractUserFromLogin(data, email);
        applySession(data.token, nextUser);
        await refreshProfile(data.token);
        return { success: true, data };
      }
      return { success: false, message: data.message || 'Login failed' };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, message: error.message || 'Network error occurred' };
    }
  };

  const logout = () => {
    clearSession();
  };

  const handleTokenExpired = () => {
    clearSession();
  };

  const value = {
    isAuthenticated,
    user,
    loading,
    token,
    login,
    logout,
    handleTokenExpired,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
